import { FC, useMemo, useState, useEffect, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { useStore } from 'hooks/useRootStore';
import { getAddress } from 'utils/hereMaps';
import {
  emailPattern,
  phonePattern,
  urlValidation,
  minLengthValidation,
  DEFAULT_NAME_MIN_LENGTH,
} from 'utils/patterns';
import { useS3ImageUploader } from 'hooks/useS3ImageUploader';
import { Button } from 'components/Core/Button';
import { TextAreaInput } from 'components/Inputs/TextAreaInput';
import SelectComponent from 'components/Inputs/SelectComponent';
import { Switch } from 'components/Core/Switch/Switch';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import LocationAutocomplete from 'components/Inputs/LocationAutocomplete';
import { ImageUploader } from 'components/Core/ImageUploader/ImageUploader';
import { MapBox } from 'components/Core/MapBox/MapBox';
import AutocompleteSelect from 'components/Inputs/AutocompleteSelect';
import EntityOption from 'components/Core/EntityOption';
import PointCategoryItem from 'components/Core/PointCategoryItem';
import { IBrandRef, ILocation } from 'types/common';
import { IPointBasicData, IPointsCategoryData } from 'stores/points/types';
import { defaultValues } from './defaults';
import { pointBasicFormTranslations } from './translations';
import { IPointBasicFormWithFile, PointBasicFormTransFields } from './types';
import { availableRoles } from './constants';
import styles from './pointBasicForm.module.scss';

const PointBasicForm: FC = observer(() => {
  const {
    pointsStore: { pointBasicData, editPoint, addPoint, pointsCategories, getPointsCategories },
    brandsStore: { getAutoCompleteBrands },
    mainStore: { user },
    alertStore: { showAlert },
  } = useStore();

  const { t, i18n } = useMyTranslations<PointBasicFormTransFields>([pointBasicFormTranslations.name]);

  const navigate = useNavigate();

  useQuery('getPointsCategories', () => (pointsCategories.length ? {} : getPointsCategories()), {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { mutate: addPointMutation, isLoading: isLoadingAdding } = useMutation(addPoint, {
    mutationKey: 'addPoint',
    onSuccess: (res: IPointBasicData) => {
      showAlert(`create point success`, 'success');
      navigate(`/points/${res.id}`);
    },
  });
  const { mutate: editPointMutation, isLoading: isLoadingChanging } = useMutation(editPoint, {
    mutationKey: 'editPoint',
    onSuccess: () => {
      showAlert(`edit point success`, 'success');
    },
  });

  const useFormMethods = useForm<IPointBasicFormWithFile>({
    defaultValues: pointBasicData || defaultValues,
  });

  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { errors, isSubmitting }, // should create errors conts to update errors when they appears
  } = useFormMethods;

  const [selectedCC, setSelectedCC] = useState('');

  useEffect(() => {
    if (pointBasicData) setSelectedCC(user?.selectedCountryCode.countryCode || '');
  }, []);

  const { uploadImage, isLoading: isImageUploading } = useS3ImageUploader<IPointBasicFormWithFile>({
    setValue,
  });

  const setLocationData = useCallback(async ({ lat, lng }: ILocation, setAddress = true) => {
    try {
      const addressData = await getAddress(lat, lng, i18n.language);
      if (!addressData) {
        showAlert('Getting address data problems was occured');
        return;
      }
      setSelectedCC(addressData.countryCode);
      setValue('zipCode', addressData.postalCode || '');
      setValue('city', addressData.city || '');
      if (setAddress) setValue('address', addressData.label || '');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      showAlert('Getting address data problems was occured: ', e.message);
    }
  }, []);

  const afterLocationSelect = useCallback(async (value: string | null, data?: { position: ILocation }) => {
    setValue('address', value || '');
    if (data) {
      setValue('latitude', data.position.lat);
      setValue('longitude', data.position.lng);
      setLocationData({ lat: data.position.lat, lng: data.position.lng }, false);
    } else {
      setValue('latitude', null);
      setValue('longitude', null);
      setValue('zipCode', '');
      setValue('city', '');
    }
  }, []);

  const afterMarkerDragged = useCallback(({ lat, lng }: ILocation) => {
    setValue('latitude', lat);
    setValue('longitude', lng);
    setLocationData({ lat, lng });
  }, []);

  const location = useMemo(() => {
    const lat = getValues('latitude');
    const lng = getValues('longitude');
    return lat && lng ? { lat, lng } : null;
  }, [watch('latitude'), watch('longitude')]);

  const onLatChange = useCallback((lat: number) => {
    const lng = getValues('longitude');
    if (lng) setLocationData({ lat, lng });
  }, []);

  const onLngChange = useCallback((lng: number) => {
    const lat = getValues('latitude');
    if (lat) setLocationData({ lat, lng });
  }, []);

  const renderBrandOption = useCallback((data: IBrandRef) => {
    return <EntityOption image={data.logoUrl} entityType='brands' name={data.name} />;
  }, []);

  const renderCategoriesOption = useCallback((data: IPointsCategoryData) => {
    return <PointCategoryItem category={data} />;
  }, []);

  const renderCategoriesValue = useCallback((data: IPointsCategoryData) => {
    return <EntityOption image={data.categoryImage} entityType='points-categories' name={data.categoryName} />;
  }, []);

  const onSubmitHandler = useCallback(
    async (inputData: IPointBasicFormWithFile) => {
      const cc = user?.selectedCountryCode.countryCode;
      if (cc !== selectedCC) {
        showAlert('Incorrect country was selected');
        return;
      }
      const { imageFile: iFile } = inputData;
      try {
        if (iFile) await uploadImage(iFile, 'image', 'points');
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { imageFile, ...data } = getValues();
        pointBasicData
          ? editPointMutation({ ...pointBasicData, ...data } as IPointBasicData)
          : addPointMutation(data as IPointBasicData);
      } catch {
        showAlert('Problems with image upload', 'warning');
      }
    },
    [pointBasicData, selectedCC]
  );

  const disabled = useMemo(() => !user || !availableRoles.includes(user.role), [user?.role]);

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <FormProvider {...(useFormMethods as any)}>
      <div className={styles.content}>
        <form className={styles.form}>
          <section className={`${styles.formSection}`}>
            <h6 className={`${styles.title} ${styles.titleWithMargin}`}>{t('title')}</h6>
            <ControlledInput
              name={`name`}
              type='text'
              required
              label={t('name')}
              disabled={disabled}
              validation={minLengthValidation(DEFAULT_NAME_MIN_LENGTH)}
            />
            <TextAreaInput name={`description`} label={t('description')} disabled={disabled} />
            <ControlledInput
              name={`phone`}
              type='text'
              label={t('phone')}
              registerPattern={phonePattern}
              disabled={disabled}
            />
            <ControlledInput
              name={`email`}
              type='text'
              label={t('email')}
              registerPattern={emailPattern}
              disabled={disabled}
            />
            <ControlledInput name={`pageUrl`} type='text' label={t('pageUrl')} validation={urlValidation()} disabled />
            <AutocompleteSelect
              name='brand'
              label={t(`brand`)}
              data={[]}
              onSearch={getAutoCompleteBrands}
              customOption={renderBrandOption}
              customSingleValue={renderBrandOption}
              isDisabled={!!pointBasicData?.brand || disabled}
              required
            />
            <SelectComponent
              name='categories'
              label={t('categories')}
              options={pointsCategories}
              optionLabel='categoryName'
              optionValue='id'
              maxMenuHeight={160}
              customOption={renderCategoriesOption}
              customMultiValue={renderCategoriesValue}
              required
              isMulti
              isDisabled={disabled}
            />
            <LocationAutocomplete
              name={`address`}
              label={t('address')}
              maxMenuHeight={200}
              afterSelect={(value, data) => {
                afterLocationSelect(value?.name || null, data);
              }}
              required
              isDisabled={disabled}
            />
            <div className={styles.locationBlock}>
              <div className={styles.checkboxWrap}>
                <h6 className={`${styles.title} ${styles.titleWithMargin}`}>{t('exactLocation')}</h6>
                <h6 className={`${styles.title} ${styles.validStatusTitle}`}>{t('isValidLocation')}</h6>
                <Switch
                  isActive={!!watch('isValidLocation')}
                  onChange={() => setValue('isValidLocation', !getValues('isValidLocation'))}
                />
              </div>
              <ControlledInput
                name='latitude'
                type='number'
                label={t('latitude')}
                myOnChange={(val) => val && onLatChange(+val)}
              />
              <ControlledInput
                name='longitude'
                type='number'
                label={t('longitude')}
                isRemoveMargin
                myOnChange={(val) => val && onLngChange(+val)}
              />
            </div>
            <div className={styles.checkboxWrap}>
              <h6 className={`${styles.title}`}>{t('status')}</h6>
              <Switch
                isActive={watch('status') === 'Active'}
                onChange={() => setValue('status', getValues('status') === 'Active' ? 'Inactive' : 'Active')}
                disabled={disabled}
              />
            </div>
          </section>
          <section className={`${styles.rightSideSection}`}>
            <ImageUploader name='image' title={t('image')} path='points' disabled={disabled} width={496} />
            {location && <MapBox locations={[location]} draggableMarkers onDragEnds={afterMarkerDragged} />}
          </section>
        </form>
        <div>
          <Button
            title={t('save')}
            onClick={handleSubmit(onSubmitHandler)}
            isDisabled={isSubmitting || isLoadingAdding || isLoadingChanging || isImageUploading}
          />
        </div>
      </div>
    </FormProvider>
  );
});

export default PointBasicForm;
